// The one list of equipment slots, shared by the Equipment screen, the movement profile and
// the server (loaded through server/utils/rogueRules.js). Saved equipment uses these keys.
// 2024 rules care about hands, one suit of armor and worn items; Shoulders, Arms, Legs and
// Ranged have no rules meaning, so they are "retired": still valid in saved data (nothing
// is deleted) but no longer offered for equipping or armor creation.

export const EQUIPMENT_SLOT_LAYOUT = [
  [
    { key: 'head', label: 'Head' },
    { key: 'eyes', label: 'Eyes' },
    { key: 'neck', label: 'Neck' },
    { key: 'shoulders', label: 'Shoulders', retired: true },
  ],
  [
    { key: 'chest', label: 'Body Armor' },
    { key: 'back', label: 'Cloak' },
    { key: 'arms', label: 'Arms', retired: true },
    { key: 'wrists', label: 'Bracers' },
  ],
  [
    { key: 'hands', label: 'Gloves' },
    { key: 'waist', label: 'Belt' },
    { key: 'legs', label: 'Legs', retired: true },
    { key: 'feet', label: 'Boots' },
  ],
  [
    { key: 'mainHand', label: 'Main Hand' },
    { key: 'offHand', label: 'Off Hand' },
    { key: 'ranged', label: 'Ranged', retired: true },
    { key: 'ringLeft', label: 'Ring I' },
    { key: 'ringRight', label: 'Ring II' },
  ],
  // 2024 limits only headwear, armor, cloaks, bracers, gloves and footwear to one each (edition audit EQP-06).
  // Amulets, eyewear, belts and rings can stack, so they get extra slots; the real cap is attunement.
  [
    { key: 'neck2', label: 'Neck II' },
    { key: 'neck3', label: 'Neck III' },
    { key: 'eyes2', label: 'Eyes II' },
    { key: 'waist2', label: 'Belt II' },
    { key: 'ring3', label: 'Ring III' },
    { key: 'ring4', label: 'Ring IV' },
  ],
];

export const EQUIPMENT_SLOTS = EQUIPMENT_SLOT_LAYOUT.flat();
export const EQUIPMENT_SLOT_KEYS = EQUIPMENT_SLOTS.map((slot) => slot.key);
export const RETIRED_EQUIPMENT_SLOT_KEYS = EQUIPMENT_SLOTS.filter((slot) => slot.retired).map((slot) => slot.key);

// Slots that hold the same kind of worn item.
export const EQUIPMENT_SLOT_FAMILIES = Object.freeze({
  neck: ['neck', 'neck2', 'neck3'],
  eyes: ['eyes', 'eyes2'],
  waist: ['waist', 'waist2'],
  ring: ['ringLeft', 'ringRight', 'ring3', 'ring4'],
});

// How the Equipment screen groups the active slots.
export const EQUIPMENT_SLOT_GROUPS = [
  { key: 'hands', label: 'Hands', slots: ['mainHand', 'offHand'] },
  { key: 'armor', label: 'Armor', slots: ['chest'] },
  { key: 'worn', label: 'Worn items', slots: ['head', 'eyes', 'eyes2', 'neck', 'neck2', 'neck3', 'back', 'waist', 'waist2', 'hands', 'wrists', 'feet', 'ringLeft', 'ringRight', 'ring3', 'ring4'] },
];

// 2024 attunement: at most three magic items at once (SRD 5.2); Use Magic Device (Thief 13) raises it to four.
export const ATTUNEMENT_LIMIT = 3;
export const USE_MAGIC_DEVICE_ATTUNEMENT_LIMIT = 4;
export const requiresAttunement = (item) => Boolean(item && typeof item === 'object'
  && item.owned !== false && (item.attunement === true || /requires attunement/i.test(String(item.attunement || ''))));
/** Equipped items that need attunement, each counted once. */
export const attunedEquipment = (equipment = {}) => {
  const seen = new Set();
  return Object.values(equipment || {}).filter((item) => {
    if (!requiresAttunement(item)) return false;
    const key = item.inventoryId || item._id || item.id || item.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
/** A message when more equipped items need attunement than the limit (three, or four with Use Magic Device), or null. */
export const getAttunementConflict = (equipment, limit = ATTUNEMENT_LIMIT) => {
  const attuned = attunedEquipment(equipment);
  return attuned.length > limit
    ? `You can be attuned to at most ${limit} magic items (equipped: ${attuned.map((item) => item.name).join(', ')}).`
    : null;
};

// Slots armor can be created for (DM armor form and AI armor generation).
export const ARMOR_SLOT_KEYS = ['head', 'chest', 'hands', 'feet', 'offHand'];
export const ARMOR_SLOT_OPTIONS = EQUIPMENT_SLOTS.filter((slot) => ARMOR_SLOT_KEYS.includes(slot.key)).map(({ key, label }) => ({ key, label }));

export const createEmptyEquipmentMap = () => Object.fromEntries(EQUIPMENT_SLOT_KEYS.map((slot) => [slot, null]));

const cloneItem = (item) => {
  if (!item) return null;
  if (typeof item === 'string') return { name: item };
  if (typeof item !== 'object') return null;
  return { ...item };
};

const getAssignmentKey = (item) => {
  if (!item || typeof item !== 'object') return '';
  // A specific inventory copy (two Scimitars) wins over the shared name; older saved
  // gear without one still collapses by name so it can't fill two slots at once.
  const identifier = item.inventoryId || item._id || item.id || item.name || item.displayName || item.title || '';
  const source = item.__source || item.source || '';
  return `${String(source).toLowerCase()}::${String(identifier).toLowerCase()}`;
};

/** Returns a map with every slot key, dropping unknown slots and moving a re-used item to its latest slot. */
export const normalizeEquipmentMap = (equipment, { fallback } = {}) => {
  const normalized = createEmptyEquipmentMap();
  if (fallback && typeof fallback === 'object') {
    EQUIPMENT_SLOT_KEYS.forEach((slot) => {
      normalized[slot] = fallback[slot] ? cloneItem(fallback[slot]) : null;
    });
  }
  if (!equipment || typeof equipment !== 'object') return normalized;

  const assigned = new Map();
  Object.entries(equipment).forEach(([slot, value]) => {
    if (!EQUIPMENT_SLOT_KEYS.includes(slot)) return;
    const cloned = value ? cloneItem(value) : null;
    if (!cloned) {
      normalized[slot] = null;
      return;
    }
    const key = getAssignmentKey(cloned);
    const previousSlot = key ? assigned.get(key) : undefined;
    if (previousSlot && previousSlot !== slot) normalized[previousSlot] = null;
    normalized[slot] = cloned;
    if (key) assigned.set(key, slot);
  });
  return normalized;
};
